import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '@daycal/ui';
import { 
  FiCalendar, FiFileText, FiShield, FiCheckCircle, 
  FiAlertTriangle, FiLock, FiGlobe, FiArrowRight, FiMail 
} from 'react-icons/fi';
import { useLocale } from '../contexts/LocaleContext.jsx';
import { useLegalCopy } from '../lib/legalContent.js';
import RichText from '../components/RichText.jsx';

function Logo() {
  return (
    <Link to="/" className="group flex items-center">
      <img src="/Day cal logo name.png" alt="DayCal Logo" className="h-16 w-auto -ml-3 -my-3 object-contain transition-transform duration-500 group-hover:scale-105" />
    </Link>
  );
}

function LangToggle({ locale, setLocale }) {
  return (
    <div className="flex items-center rounded-full bg-slate-100 p-0.5 text-xs font-semibold">
      {[['en', 'ENG'], ['bn', 'BN']].map(([code, label]) => (
        <button
          key={code}
          type="button"
          onClick={() => setLocale(code)}
          className={`rounded-full px-2.5 py-1 transition-colors ${
            locale === code ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export default function Terms() {
  const { locale, setLocale } = useLocale();
  const t = useLegalCopy('terms', locale);
  const [activeSection, setActiveSection] = useState('acceptance');

  const scrollTo = (id) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col justify-between">
      <SEO path="/terms" title={`${t.title} - DayCal`} description={t.subtitle} />
      
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5">
          <Logo />
          <nav className="hidden items-center gap-6 text-sm font-medium text-slate-600 md:flex">
            <Link to="/" className="hover:text-slate-900 transition-colors">Home</Link>
            <Link to="/privacy" className="hover:text-slate-900 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="text-brand-600 font-semibold">Terms of Service</Link>
            <Link to="/refund" className="hover:text-slate-900 transition-colors">Refund Policy</Link>
            <Link to="/contact" className="hover:text-slate-900 transition-colors">Contact Us</Link>
          </nav>
          <div className="flex items-center gap-3">
            <LangToggle locale={locale} setLocale={setLocale} />
            <Link to="/login" className="rounded-dc-sm bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-slate-800">
              Sign in
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-slate-100/70 border-b border-slate-200/60 py-16">
        <div className="pointer-events-none absolute inset-0 bg-dot-grid opacity-60" />
        <div className="pointer-events-none absolute -right-20 top-0 h-72 w-72 rounded-full bg-slate-200/50 blur-3xl" />
        <div className="relative mx-auto max-w-5xl px-5 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3.5 py-1 text-xs font-semibold text-slate-700 shadow-sm">
            <FiFileText className="h-3.5 w-3.5 text-brand-600" /> {t.badge}
          </span>
          <h1 className="font-display mt-5 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            {t.title}
          </h1>
          <p className="mt-4 max-w-2xl mx-auto text-base sm:text-lg text-slate-600 leading-relaxed">
            {t.subtitle}
          </p>
          <p className="mt-3 text-xs font-medium text-slate-400">
            Last Updated: <span className="font-semibold text-slate-600">{t.lastUpdated}</span>
          </p>
        </div>
      </section>

      {/* Content Body */}
      <main className="mx-auto max-w-6xl px-5 py-14 flex-1">
        <div className="grid gap-10 lg:grid-cols-12">
          
          {/* Index Sidebar */}
          <aside className="lg:col-span-4">
            <div className="sticky top-24 rounded-dc border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="font-display text-sm font-bold uppercase tracking-wider text-slate-400 mb-4 flex items-center gap-2">
                <FiFileText className="h-4 w-4 text-brand-600" /> Navigation
              </h3>
              <nav className="space-y-1">
                {t.toc.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollTo(item.id)}
                    className={`w-full text-left px-3 py-2 text-xs font-semibold rounded-dc-sm transition-all flex items-center justify-between ${
                      activeSection === item.id 
                        ? 'bg-brand-50 text-brand-700 font-bold border-l-2 border-brand-600' 
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <span>{item.title}</span>
                    {activeSection === item.id && <FiArrowRight className="h-3.5 w-3.5 text-brand-600" />}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Detailed Content */}
          <div className="lg:col-span-8 space-y-10">
            
            <section id="acceptance" className="rounded-dc border border-slate-200 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-dc-sm bg-brand-50 text-brand-600">
                  <FiCheckCircle className="h-6 w-6" />
                </div>
                <h2 className="font-display text-2xl font-bold text-slate-900">{t.sections.acceptance.heading}</h2>
              </div>
              <RichText className="text-slate-600 leading-relaxed text-sm" html={t.sections.acceptance.body} />
            </section>

            <section id="service" className="rounded-dc border border-slate-200 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-dc-sm bg-sky-50 text-sky-600">
                  <FiGlobe className="h-6 w-6" />
                </div>
                <h2 className="font-display text-2xl font-bold text-slate-900">{t.sections.service.heading}</h2>
              </div>
              <RichText className="text-slate-600 leading-relaxed text-sm" html={t.sections.service.body} />
            </section>

            <section id="google-integrations" className="rounded-dc border border-brand-200 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-dc-sm bg-brand-50 text-brand-600">
                  <FiLock className="h-6 w-6" />
                </div>
                <h2 className="font-display text-2xl font-bold text-slate-900">{t.sections.googleIntegrations.heading}</h2>
              </div>
              <RichText className="text-slate-600 leading-relaxed text-sm" html={t.sections.googleIntegrations.body} />
              <div className="mt-4 p-4 rounded-dc-sm bg-slate-50 border border-slate-100 text-xs text-slate-600 space-y-2">
                {t.sections.googleIntegrations.notes.map((n, i) => (
                  <RichText key={i} html={n} />
                ))}
              </div>
            </section>

            <section id="account" className="rounded-dc border border-slate-200 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-dc-sm bg-purple-50 text-purple-600">
                  <FiShield className="h-6 w-6" />
                </div>
                <h2 className="font-display text-2xl font-bold text-slate-900">{t.sections.account.heading}</h2>
              </div>
              <RichText className="text-slate-600 leading-relaxed text-sm" html={t.sections.account.body} />
            </section>

            <section id="acceptable-use" className="rounded-dc border border-slate-200 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-dc-sm bg-amber-50 text-amber-600">
                  <FiAlertTriangle className="h-6 w-6" />
                </div>
                <h2 className="font-display text-2xl font-bold text-slate-900">{t.sections.acceptableUse.heading}</h2>
              </div>
              <RichText className="text-slate-600 leading-relaxed text-sm" html={t.sections.acceptableUse.body} />
            </section>

            <section id="payments" className="rounded-dc border border-slate-200 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-dc-sm bg-emerald-50 text-emerald-600">
                  <FiFileText className="h-6 w-6" />
                </div>
                <h2 className="font-display text-2xl font-bold text-slate-900">{t.sections.payments.heading}</h2>
              </div>
              <RichText className="text-slate-600 leading-relaxed text-sm" html={t.sections.payments.body} />
            </section>

            <section id="disclaimer" className="rounded-dc border border-slate-200 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-dc-sm bg-red-50 text-red-600">
                  <FiAlertTriangle className="h-6 w-6" />
                </div>
                <h2 className="font-display text-2xl font-bold text-slate-900">{t.sections.disclaimer.heading}</h2>
              </div>
              <RichText className="text-slate-600 leading-relaxed text-sm" html={t.sections.disclaimer.body} />
            </section>

            <section id="termination" className="rounded-dc border border-slate-200 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-dc-sm bg-slate-100 text-slate-700">
                  <FiShield className="h-6 w-6" />
                </div>
                <h2 className="font-display text-2xl font-bold text-slate-900">{t.sections.termination.heading}</h2>
              </div>
              <RichText className="text-slate-600 leading-relaxed text-sm" html={t.sections.termination.body} />
            </section>

            <section id="contact" className="rounded-dc border border-slate-200 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-dc-sm bg-brand-50 text-brand-600">
                  <FiMail className="h-6 w-6" />
                </div>
                <h2 className="font-display text-2xl font-bold text-slate-900">{t.sections.contact.heading}</h2>
              </div>
              <RichText className="text-slate-600 leading-relaxed text-sm" html={t.sections.contact.body} />
              <div className="mt-4 p-5 rounded-dc-sm bg-slate-50 border border-slate-100 text-xs text-slate-700 space-y-1.5">
                <p><strong>{t.sections.contact.pocLabel}</strong> {t.sections.contact.pocValue}</p>
                <p><strong>{t.sections.contact.emailLabel}</strong> <a href={`mailto:${t.sections.contact.emailValue}`} className="text-brand-600 font-semibold underline">{t.sections.contact.emailValue}</a></p>
                <p><strong>{t.sections.contact.phoneLabel}</strong> <a href={`tel:${t.sections.contact.phoneValue.replace(/\s+/g, '')}`} className="text-brand-600 font-semibold underline">{t.sections.contact.phoneValue}</a></p>
                <p><strong>{t.sections.contact.addressLabel}</strong> {t.sections.contact.addressValue}</p>
                <p><strong>{t.sections.contact.portalLabel}</strong> <Link to="/contact" className="text-brand-600 underline font-medium">{t.sections.contact.portalValue}</Link></p>
              </div>
            </section>

          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 text-xs text-slate-500 sm:flex-row">
          <Logo />
          <p>© {new Date().getFullYear()} DayCal. All rights reserved.</p>
          <div className="flex flex-wrap gap-6 justify-center">
            <Link to="/contact" className="hover:text-slate-900 transition-colors">Contact Us</Link>
            <Link to="/privacy" className="hover:text-slate-900 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="text-brand-600 font-bold">Terms of Service</Link>
            <Link to="/refund" className="hover:text-slate-900 transition-colors">Refund Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
